import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@rudi/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader, QueryState } from '../components/page';
import { usePracticeAgent, usePrescreeningRun } from '../api/hooks';
import type { PrescreeningRunDetail } from '../api/types';
import { Fact, Facts, Section, agoText, countText, dateText } from './practice-agents';
import { stageText } from './criteria-query';
import {
  DraftBadge,
  MarkAsWrongLink,
  SourceCell,
  isHighUnknownShare,
  isUnknownRecord,
  prescreeningLoadError,
  shareText,
  trialName,
  triggerText,
} from './prescreening';

export function PrescreeningRunRoutePage() {
  const { runId } = useParams({ from: '/prescreening/runs/$runId' });
  return <PrescreeningRunDetailPage runId={runId} />;
}

export function PrescreeningRunDetailPage({ runId }: { runId: string }) {
  const run = usePrescreeningRun(runId);
  const notFound = isUnknownRecord(run.error);
  return (
    <div>
      <Link
        to="/prescreening"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Pre-screening
      </Link>
      <QueryState
        isLoading={run.isLoading}
        error={notFound ? null : prescreeningLoadError(run.error)}
      >
        {notFound ? (
          <p role="alert">
            Rudi does not know this run. Return to Pre-screening and pick it again.
          </p>
        ) : run.data ? (
          <RunView run={run.data} />
        ) : null}
      </QueryState>
    </div>
  );
}

function Total({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="rounded-3xl border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{countText(value)}</div>
    </div>
  );
}

/**
 * The way into the live view of this run's rows. Only an online practice
 * computer with the live view switched on can answer one.
 */
function LiveViewEntry({ run }: { run: PrescreeningRunDetail }) {
  const deviceId = run.source === 'practice' ? (run.deviceId ?? '') : '';
  const device = usePracticeAgent(deviceId);
  if (!deviceId || !device.data) return null;
  const note = 'max-w-xs text-right text-xs text-muted-foreground';
  if (device.data.connection !== 'connected')
    return (
      <p className={note}>
        Live view needs the practice computer online. It was last seen{' '}
        {agoText(device.data.lastSeenAt)}.
      </p>
    );
  if (device.data.lastHeartbeat?.liveViewEnabled === false)
    return <p className={note}>The practice has switched the live view off.</p>;
  return (
    <Button asChild>
      <Link
        to="/practice-agents/$deviceId/live"
        params={{ deviceId }}
        search={run.deviceRunId ? { run: run.id, deviceRun: run.deviceRunId } : { run: run.id }}
      >
        Open live view
      </Link>
    </Button>
  );
}

function RunView({ run }: { run: PrescreeningRunDetail }) {
  return (
    <>
      <PageHeader
        title={trialName(run)}
        description={`${run.source === 'patient_lists' ? 'Patient lists in Rudi' : (run.practiceName ?? 'Unnamed Practice')}, version ${run.version}${run.draft ? ', draft' : ''}`}
        actions={<LiveViewEntry run={run} />}
      />
      <div className="mb-8">
        <Facts label="Run">
          <Fact label="Practice">
            <SourceCell source={run.source} practiceId={run.practiceId} name={run.practiceName} />
          </Fact>
          <Fact label="Trial">
            {run.title ?? 'Unknown'}
            {run.catalogId && (
              <span className="ml-2 text-xs text-muted-foreground">{run.catalogId}</span>
            )}
          </Fact>
          <Fact label="Query version">
            <Link
              to="/prescreening/queries/$queryId"
              params={{ queryId: run.queryId }}
              className="underline-offset-4 hover:underline"
            >
              Version {run.version}
            </Link>
            <span className="ml-2">{run.draft ? <DraftBadge /> : 'approved'}</span>
          </Fact>
          <Fact label="Engine bundle">{run.bundleVersion ?? 'Unknown'}</Fact>
          <Fact label="Trigger">{triggerText(run.trigger)}</Fact>
          <Fact label="Started">{dateText(run.startedAt)}</Fact>
          <Fact label="Finished">{dateText(run.finishedAt)}</Fact>
          <Fact label="Received">{dateText(run.receivedAt)}</Fact>
          {run.deviceId && (
            <Fact label="Practice computer">
              <Link
                to="/practice-agents/$deviceId"
                params={{ deviceId: run.deviceId }}
                className="underline-offset-4 hover:underline"
              >
                Open computer
              </Link>
            </Fact>
          )}
          <Fact label="Doctor's review">
            {run.reviewReceivedAt ? `Received ${dateText(run.reviewReceivedAt)}` : 'None yet'}
          </Fact>
        </Facts>
      </div>

      <Section title="Totals" description="Counts from 0 to 4 show as fewer than 5.">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Total label="Screened" value={run.totals.screened} />
          <Total label="Candidates" value={run.totals.candidates} />
          <Total label="Needs review" value={run.totals.needsReview} />
          <Total label="Excluded" value={run.totals.excluded} />
        </div>
      </Section>

      <Section
        title="Criteria"
        description="Met, not met and unknown for each criterion, and what the doctor found when checking the shortlist against the record. Stage 3 criteria stay unknown until the patient model reads them."
      >
        <CriteriaTable run={run} />
      </Section>

      <Section title="Funnel" description="How many patients each stage removed.">
        <div className="overflow-x-auto rounded-3xl border">
          <Table aria-label="Funnel">
            <TableHeader>
              <TableRow>
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Entered</TableHead>
                <TableHead className="text-right">Removed</TableHead>
                <TableHead className="text-right">Removed share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {run.funnel.map(stage => (
                <TableRow key={stage.stage}>
                  <TableCell>{stageText(stage.stage)}</TableCell>
                  <TableCell className="text-right">{countText(stage.entered)}</TableCell>
                  <TableCell className="text-right">{countText(stage.removed)}</TableCell>
                  <TableCell className="text-right">{shareText(stage.removedShare)}</TableCell>
                </TableRow>
              ))}
              {run.funnel.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="h-20 text-center text-muted-foreground">
                    This run sent no funnel.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Section>
    </>
  );
}

function CriteriaTable({ run }: { run: PrescreeningRunDetail }) {
  return (
    <div className="overflow-x-auto rounded-3xl border">
      <Table aria-label="Criteria">
        <TableHeader>
          <TableRow>
            <TableHead>Criterion</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead className="text-right">Met</TableHead>
            <TableHead className="text-right">Not met</TableHead>
            <TableHead className="text-right">Unknown</TableHead>
            <TableHead className="text-right">Unknown share</TableHead>
            <TableHead className="text-right">Reviewed</TableHead>
            <TableHead className="text-right">Wrong</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {run.criteria.map(criterion => {
            const high = isHighUnknownShare(criterion);
            return (
              <TableRow key={criterion.id}>
                <TableCell className="min-w-[16rem]">
                  <div className="font-medium">
                    {criterion.id}
                    {criterion.kind && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {criterion.kind === 'exclusion' ? 'Exclusion' : 'Inclusion'}
                      </span>
                    )}
                  </div>
                  {criterion.text && (
                    <div className="text-xs text-muted-foreground">{criterion.text}</div>
                  )}
                </TableCell>
                <TableCell className="min-w-[8rem] text-xs">{stageText(criterion.stage)}</TableCell>
                <TableCell className="text-right">{countText(criterion.met)}</TableCell>
                <TableCell className="text-right">{countText(criterion.notMet)}</TableCell>
                <TableCell className="text-right">{countText(criterion.unknown)}</TableCell>
                <TableCell className={`min-w-[9rem] text-right ${high ? 'bg-destructive/10' : ''}`}>
                  <span
                    className={high ? 'font-medium text-destructive' : ''}
                    title={
                      criterion.unknownShare == null
                        ? 'Not computed, because a count is fewer than 5'
                        : undefined
                    }
                  >
                    {shareText(criterion.unknownShare)}
                  </span>
                  {high && (
                    <div className="text-xs text-destructive">often a wrong code mapping</div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {criterion.reviewed == null ? '' : countText(criterion.reviewed)}
                </TableCell>
                <TableCell className="text-right">
                  {criterion.wrong == null ? '' : countText(criterion.wrong)}
                </TableCell>
                <TableCell className="text-right">
                  <MarkAsWrongLink
                    queryId={run.queryId}
                    version={run.version}
                    criterionId={criterion.id}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          {run.criteria.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} className="h-20 text-center text-muted-foreground">
                This run sent no criterion counts.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
